// order-flow-order-detail.component.ts

import { Component, Input, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OrderFlowService } from '../order-flow.service';
import { OrderDetailItem } from '../../../model/order-detail';
import { StorageService } from '../../../core/storage.service';
import { AuthService } from '../../../core/auth/auth.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-order-flow-order-detail',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './order-flow-order-detail.component.html',
  styleUrls: ['./order-flow-order-detail.component.css'],
})
export class OrderFlowOrderDetailComponent implements OnInit {
  form!: FormGroup;
  orderDetailsGroups: OrderDetailGroup[] = []; // Array de grupos, cada um contendo campos
  orderDetails: OrderDetailField[] = []; // Array plano de todos os campos across grupos
  touchedFields: string[] = [];
  private isHandlingDependencies = false; // Flag para prevenir loops infinitos
  private helpTextVisibility: { [key: number]: boolean } = {};
  isSubmitting = false; // Flag para indicar estado de submissão
  submissionError: string | null = null; // Para armazenar erros de submissão
  isProduction = false;

  @Input() isDuplicateAction = false;
  @Input() baseOrderNumber = '';
  
  constructor(
    private fb: FormBuilder,
    private orderFlowService: OrderFlowService,
    private storageService: StorageService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.isProduction = environment && environment.production;
    if (!this.isDuplicateAction) {
      this.orderFlowService.getOrderDetailSources().subscribe(
        (response: OrderDetailResponse) => {
          let groups: OrderDetailGroup[] = response.data;

          // Therapists cannot request quotes — filter out the quote checkbox
          if (this.authService.isTherapist()) {
            groups = groups.map(group => ({
              ...group,
              fields: group.fields.filter(
                f => !f.name?.toLowerCase().includes('do you require a quote')
              )
            }));
          }

          this.orderDetailsGroups = groups;
          this.initForm();
          this.TemporarySetupBillingAddress();
        },
        (error) => {
          console.error('Error fetching order details:', error);
        },
      );
    }
  }

  initForm() {
    // Achatar todos os campos across grupos para criação de controles de formulário
    this.orderDetailsGroups.forEach((group) => {
      group.fields.sort((a, b) => a.order - b.order);

      group.fields.forEach((field) => {
        field.groupName = group.title;
      });

      this.orderDetails.push(...group.fields);
    });

    this.orderDetails = this.orderDetails.sort((a, b) => a.order - b.order);

    this.createForm();
  }

  //TODO temporary validation - We must to provide new field to configure details
  TemporarySetupBillingAddress() {
    let orderDetailValueIds: number[] = [];
    this.orderDetails.forEach((detail) => {
      if (detail.name === 'Is the shipping address different from the billing address?') {
        orderDetailValueIds.push(...detail.values.filter(value => value.value === 'Yes').map(value => value.orderDetailValueId));
      }
    });

    var billingDataHidden = this.orderDetails.filter(field => field.dependencies?.some(value => orderDetailValueIds.includes(value.orderDetailValueId))
    );

    billingDataHidden.forEach((field) => {
      field.isHidden = false;
      const control = this.form.get(field.name);
      if (control) {
      control.enable({ emitEvent: true });
      control.updateValueAndValidity({ emitEvent: true });
      }
    });
  }

  /**
   * Populates the form with existing order details.
   * @param baseOrderDetails The order details to populate.
   */
  populateForm(baseOrderDetails: OrderDetailGroup[]): void {
    // Assuming baseOrderDetails is an array of groups with their fields
    baseOrderDetails.forEach((group) => {
      group.fields.forEach((field) => {
        const control = this.form.get(field.name);
        if (control) {
          // Find the corresponding field in orderDetails
          const duplicatedField = this.orderDetails.find(
            (d) => d.orderDetailFieldId === field.orderDetailFieldId,
          );
          if (duplicatedField) {
            control.setValue(
              field.selectedValue || duplicatedField.selectedValue || '',
            );
          }
        }
      });
    });

    // Optionally, trigger change detection or handle dependencies
    this.handleDependencies();
  }

  createForm() {
    const group: any = {};

    this.orderDetails.forEach((detail) => {
      // Determine initial value from local storage or default
      const storedValue = this.storageService.getItem(detail.name);
      const initialValue = storedValue !== null && storedValue !== undefined
      ? storedValue
      : detail.selectedValue || (
          detail.name === 'Do you require a quote?' ? 'Yes' : ''
        );
      const validators =
        detail.isRequired && detail.isEnable && !detail.isHidden
          ? [Validators.required]
          : [];

      group[detail.name] = [
        { value: initialValue, disabled: !detail.isEnable },
        validators,
      ];
    });

    this.form = this.fb.group(group);

    Object.keys(this.form.controls).forEach((fieldName) => {
      const control = this.form.get(fieldName);

      control?.valueChanges.subscribe(() => {
        if (
          control.dirty &&
          !this.touchedFields.includes(fieldName)
        ) {
          this.touchedFields.push(fieldName);
        }
      });
    });

    // Listen to form changes for dynamic dependencies
    this.form.valueChanges.subscribe(() => {
      if (!this.isHandlingDependencies) {
        this.handleDependencies();
      }
    });
  }

  handleDependencies() {
    this.isHandlingDependencies = true;

    this.orderDetails.forEach((detail) => {
      if (detail.dependencies && detail.dependencies.length > 0) {
        const shouldBeVisible = this.shouldFieldBeVisible(detail);
        detail.isHidden = !shouldBeVisible;

        if (shouldBeVisible) {
          if (detail.isRequired) {
            this.form.get(detail.name)?.setValidators([Validators.required]);
          }
          this.form.get(detail.name)?.enable({ emitEvent: false });
        } else {
          this.form.get(detail.name)?.clearValidators();
          this.form.get(detail.name)?.disable({ emitEvent: false });
        }

        this.form
          .get(detail.name)
          ?.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.isHandlingDependencies = false;
  }

  shouldFieldBeVisible(detail: OrderDetailField): boolean {
    return detail.dependencies!.every((dependency: Dependency) => {
      const dependentField = this.orderDetails.find(
        (d) => d.orderDetailFieldId === dependency.orderDetailFieldId,
      );

      if (dependentField) {
        const selectedValue = this.form.get(dependentField.name)?.value;

        // Find the corresponding orderDetailValueId based on the selected value
        const selectedValueId = dependentField.values.find(
          (val) => val.value === selectedValue,
        )?.orderDetailValueId;

        const result = selectedValueId === dependency.orderDetailValueId;
        return result;
      }

      return false;
    });
  }

  recoveryDataFromLocalStorage(name: string, value: string) {
    //TODO
    if (value) {
      return value
    }
    else {
      const result = this.storageService.getItem(name);
      this.form.get(name)?.setValue(result);
      return result;
    }
  }

  showHelpText(fieldId: number) {
    this.helpTextVisibility[fieldId] = true;
  }

  hideHelpText(fieldId: number) {
    this.helpTextVisibility[fieldId] = false;
  }

  isHelpTextVisible(fieldId: number): boolean {
    return !!this.helpTextVisibility[fieldId];
  }

  // Método para montar o payload
  assemblePayload(): any[] {
    return this.orderDetails.map((detail) => ({
      orderDetailsSourceId: detail.orderDetailFieldId,
      orderDetailsSourceValue: this.form.get(detail.name)?.value || '',
      orderDetailsSourceValueId: detail.values.find(
        (val) => val.value === this.form.get(detail.name)?.value || '',
      )?.orderDetailValueId,
    }));
  }

  assembleSummaryPayload(): OrderDetailItem[] {
    return this.orderDetails.map((detail) => ({
      label: detail.name,
      value: this.form.get(detail.name)?.value || '',
      group: detail.groupName,
    }));
  }

  // Método para verificar se o formulário é válido
  isValid(): boolean {
    return this.form.valid;
  }

  // Método para marcar todos os campos como tocados (usado para exibir erros de validação)
  markAllAsTouched() {
    this.form.markAllAsTouched();
  }

  // Método opcional para obter o payload para uso externo
  getPayload(): any[] {
    return this.assemblePayload();
  }

  // Método opcional para resetar o formulário
  resetForm() {
    this.form.reset();
  }

  autofillFieldsTemporary(): void {
    if (environment && environment.production) {
      return;
    }

    this.orderDetailsGroups.forEach((group) => {
      group.fields.forEach((field) => {
        const control = this.form.get(field.name);

        if (field.name === 'Patient\'s Name') {
          control?.setValue('Customer Name');
        }
        else if (field.name === 'Gender') {
          control?.setValue('Female');
        } else if (field.name === 'Measured By - Name') {
          control?.setValue('Doctor Name');
        } else if (field.name === 'Measured By - Organisation') {
          control?.setValue('Doctor Organisation');
        } else if (field.name === 'Measurer Email') {
          control?.setValue('sigvaris@test.com');
        } else if (field.name === 'Measurer Telephone') {
          control?.setValue('98763787654');
        } else if (field.name === 'Measured Date') {
          control?.setValue(new Date().toISOString().split('T')[0]);
        } else if (field.name === 'Do you need a quote?') {
          control?.setValue('No');
        } else if (field.name === 'Do you have a Purchase Order Number?') {
          control?.setValue('Purchase Order number to be generated');
        } else if (field.name === 'Payee Name') {
          control?.setValue('Payee Name');
        } else if (field.name === 'Is the shipping address different from the billing address?') {
          control?.setValue('No');
        }
      });
    });

    //this.handleDependencies();
  }
}

// Interfaces permanecem inalteradas
interface OrderDetailValue {
  orderDetailValueId: number;
  value: string;
}

interface Dependency {
  orderDetailFieldId: number;
  orderDetailValueId: number;
  objectType: string;
}

interface OrderDetailField {
  orderDetailFieldId: number;
  name: string;
  componentType: string;
  selectionType: string;
  helpText: string | null;
  values: OrderDetailValue[];
  isVisible: boolean;
  isEnable: boolean;
  isHidden: boolean;
  order: number;
  isRequired?: boolean;
  dependencies?: Dependency[];
  groupName: string;
  selectedValue: string;
}

interface OrderDetailGroup {
  title: string;
  helpText: string;
  fields: OrderDetailField[];
}

interface OrderDetailResponse {
  data: OrderDetailGroup[];
}